import React from 'react';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

const sectionShellClass = 'surface-card p-4 border-round-xl shadow-1 mb-4';

const FieldLabel = ({ htmlFor, required, children }) => (
    <label htmlFor={htmlFor} className="block text-900 font-medium mb-2">
        {children}
        {required ? <span className="text-red-500"> *</span> : null}
    </label>
);

const SectionHeader = ({ iconClass, title }) => (
    <div className="flex align-items-center mb-4">
        <i className={`${iconClass} text-2xl text-primary mr-3`} />
        <h3 className="m-0 text-xl font-semibold">{title}</h3>
    </div>
);

const VehiclePlaceOrderForm = ({
    th,
    brands,
    paymentTermsList,
    paymentTermsLoading,
    initialData,
    initialLoading,
    brand,
    model,
    matnr,
    quantity,
    deliveryDate,
    paymentTerms,
    paymentDate,
    modelOptions,
    materialOptions,
    modelsLoading,
    materialsLoading,
    minDate,
    submitting,
    validationMessageKey,
    onBrandChange,
    onModelChange,
    onMaterialChange,
    setQuantity,
    setDeliveryDate,
    setPaymentTerms,
    setPaymentDate,
    handleSubmit
}) => {
    const currencyDisplay = initialData?.currency ?? '';
    const meinsText = th('vehicleSalesPlaceOrder.meinsValue');

    return (
        <div className="card">
            <div className="mb-4">
                <h1 className="m-0 mb-2">{th('vehicleSalesPlaceOrder.pageTitle')}</h1>
                <p className="m-0 text-color-secondary line-height-3">{th('vehicleSalesPlaceOrder.pageSubtitle')}</p>
            </div>

            {validationMessageKey ? (
                <Message severity="warn" text={th(validationMessageKey)} className="mb-4 w-full border-round-lg" />
            ) : null}

            {/* Araç bilgileri */}
            <div className={sectionShellClass}>
                <SectionHeader iconClass="pi pi-car" title={th('vehicleSalesPlaceOrder.sections.vehicle')} />
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-brand" required>
                            {th('vehicleSalesPlaceOrder.brand')}
                        </FieldLabel>
                        <Dropdown
                            id="vo-brand"
                            value={brand}
                            options={brands}
                            optionLabel="label"
                            optionValue="value"
                            onChange={onBrandChange}
                            placeholder={th('vehicleSalesPlaceOrder.selectBrand')}
                            filter
                            showClear
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-model" required>
                            {th('vehicleSalesPlaceOrder.model')}
                        </FieldLabel>
                        <Dropdown
                            id="vo-model"
                            value={model}
                            options={modelOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={onModelChange}
                            placeholder={th('vehicleSalesPlaceOrder.selectModel')}
                            disabled={!brand || modelsLoading}
                            filter
                            showClear
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-material" required>
                            {th('vehicleSalesPlaceOrder.material')}
                        </FieldLabel>
                        <Dropdown
                            id="vo-material"
                            value={matnr}
                            options={materialOptions}
                            optionLabel="displayText"
                            optionValue="matnr"
                            onChange={onMaterialChange}
                            placeholder={th('vehicleSalesPlaceOrder.selectMaterial')}
                            disabled={!brand || !model || materialsLoading}
                            filter
                            showClear
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            <Divider />

            {/* Para birimi ve miktar */}
            <div className={sectionShellClass}>
                <SectionHeader iconClass="pi pi-wallet" title={th('vehicleSalesPlaceOrder.sections.financial')} />
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-currency">{th('vehicleSalesPlaceOrder.currency')}</FieldLabel>
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-money-bill" />
                            </span>
                            <InputText
                                id="vo-currency"
                                readOnly
                                className="w-full"
                                value={initialLoading ? th('common.loading') : currencyDisplay}
                            />
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-meins">{th('vehicleSalesPlaceOrder.meins')}</FieldLabel>
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-box" />
                            </span>
                            <InputText id="vo-meins" readOnly className="w-full" value={meinsText} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-qty" required>
                            {th('vehicleSalesPlaceOrder.quantity')}
                        </FieldLabel>
                        <InputNumber
                            id="vo-qty"
                            value={quantity}
                            onValueChange={(e) => {
                                const raw = e.value;
                                if (raw == null || raw === '') {
                                    setQuantity(1);
                                    return;
                                }
                                const n = Number(raw);
                                if (Number.isNaN(n)) return;
                                const intVal = Math.floor(Math.abs(n));
                                setQuantity(intVal < 1 ? 1 : intVal);
                            }}
                            min={1}
                            step={1}
                            minFractionDigits={0}
                            maxFractionDigits={0}
                            useGrouping={false}
                            className="w-full"
                            inputClassName="w-full"
                        />
                    </div>
                </div>
            </div>

            <Divider />

            {/* Teslimat ve ödeme */}
            <div className={sectionShellClass}>
                <SectionHeader iconClass="pi pi-calendar" title={th('vehicleSalesPlaceOrder.sections.schedule')} />
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-delivery" required>
                            {th('vehicleSalesPlaceOrder.deliveryDate')}
                        </FieldLabel>
                        <Calendar
                            id="vo-delivery"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.value)}
                            minDate={minDate}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-payterms" required>
                            {th('vehicleSalesPlaceOrder.paymentTerms')}
                        </FieldLabel>
                        <Dropdown
                            id="vo-payterms"
                            value={paymentTerms}
                            options={paymentTermsList}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setPaymentTerms(e.value)}
                            placeholder={th('vehicleSalesPlaceOrder.selectPaymentTerms')}
                            filter
                            showClear
                            disabled={paymentTermsLoading}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <FieldLabel htmlFor="vo-paydate" required>
                            {th('vehicleSalesPlaceOrder.paymentDate')}
                        </FieldLabel>
                        <Calendar
                            id="vo-paydate"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.value)}
                            minDate={minDate}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            <Divider />

            <div className="flex justify-content-start gap-3 pt-2">
                <Button
                    type="button"
                    label={th('vehicleSalesPlaceOrder.submit')}
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={handleSubmit}
                    loading={submitting}
                />
            </div>
        </div>
    );
};

export default React.memo(VehiclePlaceOrderForm);
